
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { 
  Tabs, TabsContent, TabsList, TabsTrigger 
} from "@/components/ui/tabs";
import { 
  Card, CardContent, CardDescription, CardHeader, CardTitle 
} from "@/components/ui/card";
import { 
  Button 
} from "@/components/ui/button";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from "@/components/ui/dialog";
import { 
  Form, FormField, FormItem, FormLabel, FormControl, FormMessage
} from "@/components/ui/form";
import { 
  Input 
} from "@/components/ui/input";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, AlertCircle, ArrowRight, Utensils, Info, ChevronRight, ChevronDown, Search, Download, Bookmark, List } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO, startOfWeek, endOfWeek, isSameDay, startOfDay, endOfDay } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { RecipeDetails } from "@/components/RecipeDetails";
import { GroceryList } from "@/components/GroceryList";
import { PdfDownloader } from "@/components/PdfDownloader";
import { BarChart, PieChart, Cell, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Pie } from "recharts";
import { SavedRecipesTab } from "./SavedRecipesTab";
import { GroceryListTab } from "./GroceryListTab";

interface MealLog {
  id: string;
  user_id: string;
  meal_description: string;
  meal_time: string;
  date: string;
  created_at: string;
  calories?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
}

interface RecipeResponse {
  recipeName: string;
  ingredients: string[];
  instructions: string;
}

interface NutrientBreakdown {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

interface SuggestedMeal {
  name: string;
  ingredients: string[];
  nutrient_breakdown: NutrientBreakdown;
  recipe?: {
    recipeName: string;
    ingredients: string[];
    instructions: string;
  };
}

interface NutritionAdvice {
  recommendation: string;
  suggested_meals: SuggestedMeal[];
  overall_nutrient_breakdown: NutrientBreakdown;
  warnings?: string[];
}

interface MealFormValues {
  mealDescription: string;
  mealTime: string;
  date: string;
  calories: string;
  protein: string;
  carbs: string;
  fat: string;
}

interface DailyMacros {
  date: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

const NutritionDashboard = () => {
  const { user } = useAuth();
  const form = useForm<MealFormValues>({
    defaultValues: {
      mealDescription: "",
      mealTime: "breakfast",
      date: new Date().toISOString().slice(0, 10),
      calories: "",
      protein: "",
      carbs: "",
      fat: ""
    }
  });
  
  // Data state
  const [mealLogs, setMealLogs] = useState<MealLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  
  // Nutrition advice
  const [nutritionAdvice, setNutritionAdvice] = useState<NutritionAdvice | null>(null);
  const [showNutritionDialog, setShowNutritionDialog] = useState(false);
  const [isGeneratingAdvice, setIsGeneratingAdvice] = useState(false);
  const [selectedMealId, setSelectedMealId] = useState<string | null>(null);
  const [expandedMeal, setExpandedMeal] = useState<string | null>(null);
  const [selectedMealDetails, setSelectedMealDetails] = useState<MealLog | null>(null);
  
  // Expanded meal states
  const [showRecipeFor, setShowRecipeFor] = useState<number | null>(null);
  const [showGroceryFor, setShowGroceryFor] = useState<number | null>(null);
  
  // Macro tracking states
  const [dailyMacros, setDailyMacros] = useState<DailyMacros[]>([]);
  const [todayMacros, setTodayMacros] = useState<NutrientBreakdown>({
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0
  });
  const [weeklyMacros, setWeeklyMacros] = useState<NutrientBreakdown>({
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0
  });
  const [showMacroStats, setShowMacroStats] = useState(false);
  
  const fetchMealLogs = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('meal_logs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error("Error fetching meal logs:", error);
        toast({
          title: "Error",
          description: "Failed to load meal logs",
          variant: "destructive"
        });
      }
      
      setMealLogs(data || []);
      
      // Calculate macros after fetching data
      calculateMacros(data || []);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchMealLogs();
  }, [user]);
  
  const calculateMacros = (meals: MealLog[]) => {
    const today = new Date();
    const weekStart = startOfWeek(today);
    const weekEnd = endOfWeek(today);
    
    // Calculate today's macros
    const todayMeals = meals.filter(meal => {
      const mealDate = new Date(meal.date);
      return isSameDay(mealDate, today);
    });
    
    const todayTotals = {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0
    };
    
    todayMeals.forEach(meal => {
      todayTotals.calories += meal.calories || 0;
      todayTotals.protein += meal.protein || 0;
      todayTotals.carbs += meal.carbs || 0;
      todayTotals.fat += meal.fat || 0;
    });
    
    setTodayMacros(todayTotals);
    
    // Calculate weekly macros
    const weeklyMeals = meals.filter(meal => {
      const mealDate = new Date(meal.date);
      return mealDate >= weekStart && mealDate <= weekEnd;
    });
    
    const weeklyTotals = {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0
    };
    
    weeklyMeals.forEach(meal => {
      weeklyTotals.calories += meal.calories || 0;
      weeklyTotals.protein += meal.protein || 0;
      weeklyTotals.carbs += meal.carbs || 0;
      weeklyTotals.fat += meal.fat || 0;
    });
    
    setWeeklyMacros(weeklyTotals);
    
    // Create daily breakdown for chart
    const uniqueDates = [...new Set(weeklyMeals.map(meal => meal.date.split('T')[0]))];
    const dailyBreakdown = uniqueDates.map(date => {
      const mealsOnDate = weeklyMeals.filter(meal => meal.date.split('T')[0] === date);
      const total = {
        date: format(new Date(date), 'MMM d'),
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0
      };
      
      mealsOnDate.forEach(meal => {
        total.calories += meal.calories || 0;
        total.protein += meal.protein || 0;
        total.carbs += meal.carbs || 0;
        total.fat += meal.fat || 0;
      });
      
      return total;
    });
    
    // Sort the daily breakdown by date
    dailyBreakdown.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    setDailyMacros(dailyBreakdown);
  };
  
  const submitMealLog = async (values: MealFormValues) => {
    if (!user) return;
    
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase
        .from('meal_logs')
        .insert({
          user_id: user.id,
          meal_description: values.mealDescription,
          meal_time: values.mealTime,
          date: values.date,
          calories: values.calories ? parseInt(values.calories) : null,
          protein: values.protein ? parseFloat(values.protein) : null,
          carbs: values.carbs ? parseFloat(values.carbs) : null,
          fat: values.fat ? parseFloat(values.fat) : null
        });
        
      if (error) {
        console.error("Error submitting meal log:", error);
        toast({
          title: "Error",
          description: "Failed to save meal log",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success",
          description: "Meal log saved successfully",
        });
        setShowAddForm(false);
        form.reset();
        fetchMealLogs();
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Function to add a suggested meal to meal log
  const addSuggestedMeal = async (meal: SuggestedMeal) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('meal_logs')
        .insert({
          user_id: user.id,
          meal_description: meal.name,
          meal_time: "lunch", // Default to lunch, can be adjusted based on time of day
          date: new Date().toISOString().slice(0, 10),
          calories: meal.nutrient_breakdown.calories,
          protein: meal.nutrient_breakdown.protein,
          carbs: meal.nutrient_breakdown.carbs,
          fat: meal.nutrient_breakdown.fat
        });
        
      if (error) {
        console.error("Error adding suggested meal:", error);
        toast({
          title: "Error",
          description: "Failed to add meal to log",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success",
          description: "Meal added to your log",
        });
        fetchMealLogs();
      }
    } catch (error) {
      console.error("Error adding suggested meal:", error);
      toast({
        title: "Error",
        description: "Failed to add meal to log",
        variant: "destructive"
      });
    }
  };

  // Function to save a suggested recipe
  const saveRecipe = async (recipe: RecipeResponse) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('recipes')
        .insert({
          user_id: user.id,
          recipe_name: recipe.recipeName,
          ingredients: recipe.ingredients,
          instructions: recipe.instructions
        });
        
      if (error) {
        console.error("Error saving recipe:", error);
        toast({
          title: "Error",
          description: "Failed to save recipe",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success",
          description: "Recipe saved successfully",
        });
      }
    } catch (error) {
      console.error("Error saving recipe:", error);
      toast({
        title: "Error",
        description: "Failed to save recipe",
        variant: "destructive"
      });
    }
  };
  
  const generateNutritionAdvice = async (mealId?: string) => {
    if (!user) return;
    
    setIsGeneratingAdvice(true);
    setSelectedMealId(mealId || null);
    
    try {
      // Find the selected meal if an ID was provided
      let selectedMeal: MealLog | null = null;
      if (mealId) {
        selectedMeal = mealLogs.find(meal => meal.id === mealId) || null;
        setSelectedMealDetails(selectedMeal);
      }
      
      // Get user profile for personalized recommendations
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();
        
      if (profileError) {
        throw new Error("Could not fetch user profile");
      }
      
      // Get session token for authentication
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error("Authentication required");
      }
      
      // Call the edge function
      const { data, error } = await supabase.functions.invoke('nutrition-recommendations', {
        body: { 
          userProfile,
          recentMeals: mealLogs.slice(0, 7),
          currentMeal: selectedMeal?.meal_description || null,
          selectedMealId: mealId || null
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      if (error) {
        throw error;
      }
      
      // Increment credits used
      await supabase.rpc('increment_credits_used', { increment_by: 1 });
      
      setNutritionAdvice(data);
      setShowNutritionDialog(true);
      
      toast({
        title: "Recommendations Generated",
        description: "Vita has created personalized recommendations for you.",
      });
      
    } catch (error) {
      console.error("Error getting nutrition advice:", error);
      toast({
        title: "Error",
        description: "Could not generate nutrition recommendations. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingAdvice(false);
    }
  };
  
  const toggleMealExpansion = (mealId: string) => {
    if (expandedMeal === mealId) {
      setExpandedMeal(null);
    } else {
      setExpandedMeal(mealId);
    }
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(
    mealLogs.filter(meal => meal.meal_description.toLowerCase().includes(searchTerm.toLowerCase())).length / perPage
  );
  
  const paginatedMeals = mealLogs
    .filter(meal => meal.meal_description.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice((currentPage - 1) * perPage, currentPage * perPage);

  // Calculate macro percentages for pie chart
  const macroPercentages = [
    { name: 'Protein', value: todayMacros.protein, color: '#FF6384' },
    { name: 'Carbs', value: todayMacros.carbs, color: '#36A2EB' },
    { name: 'Fat', value: todayMacros.fat, color: '#FFCE56' }
  ];
  
  const COLORS = ['#FF6384', '#36A2EB', '#FFCE56'];
  
  return (
    <div className="space-y-4 p-1">
      <Tabs defaultValue="logs" className="w-full">
        <TabsList className="w-full mb-4">
          <TabsTrigger value="logs" className="flex-1">Meal Logs</TabsTrigger>
          <TabsTrigger value="saved-recipes" className="flex-1">
            <Bookmark className="w-4 h-4 mr-2" />
            Saved Recipes
          </TabsTrigger>
          <TabsTrigger value="grocery-list" className="flex-1">
            <List className="w-4 h-4 mr-2" />
            Grocery List
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <CardTitle>Meal Log</CardTitle>
                  <CardDescription>Track your daily meals and nutrition</CardDescription>
                </div>
                <Button onClick={() => setShowAddForm(true)} className="ml-auto">
                  <Plus className="w-4 h-4 mr-2" /> Add Meal
                </Button>
              </div>
              
              <div className="flex items-center mt-2 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
                <Input
                  placeholder="Search meals..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page on search
                  }}
                  className="pl-9"
                />
              </div>
            </CardHeader>
            
            <CardContent>
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : paginatedMeals.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No meals logged yet.</p>
                  <Button onClick={() => setShowAddForm(true)} variant="outline" className="mt-4">
                    Log your first meal
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {paginatedMeals.map((meal) => (
                    <div key={meal.id} className="border rounded-lg p-4">
                      <div 
                        className="flex justify-between items-start cursor-pointer" 
                        onClick={() => toggleMealExpansion(meal.id)}
                      >
                        <div>
                          <div className="flex items-center">
                            <h3 className="font-medium capitalize">{meal.meal_time}</h3>
                            <span className="mx-2 text-muted-foreground">•</span>
                            <span className="text-sm text-muted-foreground">
                              {format(parseISO(meal.date), 'MMM d, yyyy')}
                            </span>
                          </div>
                          <p className="mt-1">{meal.meal_description}</p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              generateNutritionAdvice(meal.id);
                            }}
                          >
                            <Info className="h-4 w-4 mr-1" />
                            Get Nutrition Advice
                          </Button>
                          {expandedMeal === meal.id ? (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                      
                      {expandedMeal === meal.id && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-sm text-muted-foreground">Calories</p>
                              <p className="font-medium">{meal.calories || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Protein</p>
                              <p className="font-medium">{meal.protein ? `${meal.protein}g` : 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Carbs</p>
                              <p className="font-medium">{meal.carbs ? `${meal.carbs}g` : 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-muted-foreground">Fat</p>
                              <p className="font-medium">{meal.fat ? `${meal.fat}g` : 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-6">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                  
                  {/* Nutrition Advice Button */}
                  <div className="flex justify-center mt-6">
                    <Button 
                      onClick={() => generateNutritionAdvice()} 
                      disabled={isGeneratingAdvice || mealLogs.length === 0}
                      className="flex items-center gap-1"
                    >
                      {isGeneratingAdvice ? (
                        <>
                          <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-1"></span>
                          Generating...
                        </>
                      ) : (
                        <>
                          <Info className="h-4 w-4 mr-1" />
                          Get General Nutrition Advice
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Macro Tracking Stats Button */}
                  <div className="flex justify-center mt-2">
                    <Button 
                      variant="outline"
                      onClick={() => setShowMacroStats(!showMacroStats)}
                      className="flex items-center gap-1"
                    >
                      {showMacroStats ? "Hide Macro Stats" : "Show Macro Stats"}
                    </Button>
                  </div>
                  
                  {/* Macro Tracking Stats */}
                  {showMacroStats && (
                    <div className="mt-6">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Macro Nutrition Tracking</CardTitle>
                          <CardDescription>Track your daily and weekly macro intake</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-6">
                            {/* Today's Macros */}
                            <div>
                              <h3 className="text-base font-medium mb-3">Today's Macros</h3>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                                <div className="bg-primary/10 p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">Calories</p>
                                  <p className="text-xl font-semibold">{todayMacros.calories}</p>
                                  <p className="text-xs">kcal</p>
                                </div>
                                <div className="bg-primary/10 p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">Protein</p>
                                  <p className="text-xl font-semibold">{todayMacros.protein}</p>
                                  <p className="text-xs">grams</p>
                                </div>
                                <div className="bg-primary/10 p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">Carbs</p>
                                  <p className="text-xl font-semibold">{todayMacros.carbs}</p>
                                  <p className="text-xs">grams</p>
                                </div>
                                <div className="bg-primary/10 p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">Fat</p>
                                  <p className="text-xl font-semibold">{todayMacros.fat}</p>
                                  <p className="text-xs">grams</p>
                                </div>
                              </div>
                              
                              {/* Macro Distribution Pie Chart */}
                              <div className="h-80 mt-4">
                                <h4 className="text-sm font-medium mb-2 text-center">Macro Distribution</h4>
                                <ResponsiveContainer width="100%" height="100%">
                                  <PieChart>
                                    <Pie
                                      data={macroPercentages}
                                      cx="50%"
                                      cy="50%"
                                      labelLine={false}
                                      outerRadius={80}
                                      fill="#8884d8"
                                      dataKey="value"
                                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    >
                                      {macroPercentages.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                      ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => `${value}g`} />
                                    <Legend />
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                            
                            <Separator />
                            
                            {/* Weekly Macros */}
                            <div>
                              <h3 className="text-base font-medium mb-3">Weekly Overview</h3>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                                <div className="bg-primary/10 p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">Total Calories</p>
                                  <p className="text-xl font-semibold">{weeklyMacros.calories}</p>
                                  <p className="text-xs">kcal</p>
                                </div>
                                <div className="bg-primary/10 p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">Total Protein</p>
                                  <p className="text-xl font-semibold">{weeklyMacros.protein}</p>
                                  <p className="text-xs">grams</p>
                                </div>
                                <div className="bg-primary/10 p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">Total Carbs</p>
                                  <p className="text-xl font-semibold">{weeklyMacros.carbs}</p>
                                  <p className="text-xs">grams</p>
                                </div>
                                <div className="bg-primary/10 p-3 rounded-lg">
                                  <p className="text-xs text-muted-foreground">Total Fat</p>
                                  <p className="text-xl font-semibold">{weeklyMacros.fat}</p>
                                  <p className="text-xs">grams</p>
                                </div>
                              </div>
                              
                              {/* Weekly Trend Bar Chart */}
                              <div className="h-80 mt-4">
                                <h4 className="text-sm font-medium mb-2 text-center">Daily Calorie Intake</h4>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart
                                    data={dailyMacros}
                                    margin={{
                                      top: 5,
                                      right: 30,
                                      left: 20,
                                      bottom: 5,
                                    }}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis label={{ value: 'Calories', angle: -90, position: 'insideLeft' }} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="calories" fill="#8884d8" name="Calories" />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                              
                              {/* Macronutrient Trend Bar Chart */}
                              <div className="h-80 mt-6">
                                <h4 className="text-sm font-medium mb-2 text-center">Daily Macronutrient Intake</h4>
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart
                                    data={dailyMacros}
                                    margin={{
                                      top: 5,
                                      right: 30,
                                      left: 20,
                                      bottom: 5,
                                    }}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis label={{ value: 'Grams', angle: -90, position: 'insideLeft' }} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="protein" fill="#FF6384" name="Protein" />
                                    <Bar dataKey="carbs" fill="#36A2EB" name="Carbs" />
                                    <Bar dataKey="fat" fill="#FFCE56" name="Fat" />
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="saved-recipes">
          <SavedRecipesTab />
        </TabsContent>

        <TabsContent value="grocery-list">
          <GroceryListTab />
        </TabsContent>
      </Tabs>
      
      {/* Meal Log Form Dialog */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Meal</DialogTitle>
            <DialogDescription>
              Enter the details of your meal below.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(submitMealLog)} className="space-y-4">
              <FormField
                control={form.control}
                name="mealDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Meal Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="E.g., Grilled chicken with roasted vegetables and quinoa" 
                        {...field}
                        required
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="mealTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Meal Time</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a meal time" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="breakfast">Breakfast</SelectItem>
                          <SelectItem value="lunch">Lunch</SelectItem>
                          <SelectItem value="dinner">Dinner</SelectItem>
                          <SelectItem value="snack">Snack</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date</FormLabel>
                      <FormControl>
                        <Input 
                          type="date" 
                          {...field}
                          required
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="calories"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Calories</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="kcal"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="protein"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Protein</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="g"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="carbs"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Carbs</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="g"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="fat"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fat</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="g"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={isSubmitting || !form.getValues().mealDescription}
                >
                  {isSubmitting ? 'Saving...' : 'Save Meal'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      
      {/* Nutrition Advice Dialog */}
      <Dialog open={showNutritionDialog} onOpenChange={setShowNutritionDialog}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nutrition Recommendations</DialogTitle>
            {selectedMealDetails && (
              <DialogDescription>
                Based on your meal: {selectedMealDetails.meal_description}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {nutritionAdvice ? (
            <ScrollArea className="max-h-[70vh] pr-4">
              <div className="space-y-6">
                {/* Recommendation */}
                <div>
                  <h3 className="text-lg font-semibold mb-2">Personalized Recommendation</h3>
                  <p className="text-sm">{nutritionAdvice.recommendation}</p>
                </div>
                
                {/* Warnings */}
                {nutritionAdvice.warnings && nutritionAdvice.warnings.length > 0 && (
                  <Alert>
                    <AlertCircle className="h-4 w-4 mr-2" />
                    <AlertDescription>
                      <ul className="list-disc list-inside">
                        {nutritionAdvice.warnings.map((warning, i) => (
                          <li key={i}>{warning}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Nutrient Breakdown */}
                <div>
                  <h3 className="text-lg font-semibold mb-2">Daily Nutrient Recommendations</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">Calories</p>
                      <p className="text-xl font-semibold">{nutritionAdvice.overall_nutrient_breakdown.calories}</p>
                      <p className="text-xs">kcal</p>
                    </div>
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">Protein</p>
                      <p className="text-xl font-semibold">{nutritionAdvice.overall_nutrient_breakdown.protein}</p>
                      <p className="text-xs">grams</p>
                    </div>
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">Carbs</p>
                      <p className="text-xl font-semibold">{nutritionAdvice.overall_nutrient_breakdown.carbs}</p>
                      <p className="text-xs">grams</p>
                    </div>
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground">Fat</p>
                      <p className="text-xl font-semibold">{nutritionAdvice.overall_nutrient_breakdown.fat}</p>
                      <p className="text-xs">grams</p>
                    </div>
                  </div>
                </div>
                
                {/* Suggested Meals */}
                <div>
                  <h3 className="text-lg font-semibold mb-2">Suggested Meals</h3>
                  <div className="space-y-6">
                    {nutritionAdvice.suggested_meals.map((meal, index) => (
                      <Card key={index} className="overflow-hidden">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex justify-between items-start">
                            <span>{meal.name}</span>
                            <div className="flex gap-2">
                              {meal.recipe && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => saveRecipe(meal.recipe)}
                                >
                                  <Bookmark className="w-3 h-3 mr-1" /> Save Recipe
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                onClick={() => addSuggestedMeal(meal)}
                              >
                                <Plus className="w-3 h-3 mr-1" /> Log Meal
                              </Button>
                            </div>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pb-3">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm mb-3">
                            <div>
                              <p className="text-xs text-muted-foreground">Calories</p>
                              <p className="font-medium">{meal.nutrient_breakdown.calories} kcal</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Protein</p>
                              <p className="font-medium">{meal.nutrient_breakdown.protein}g</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Carbs</p>
                              <p className="font-medium">{meal.nutrient_breakdown.carbs}g</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Fat</p>
                              <p className="font-medium">{meal.nutrient_breakdown.fat}g</p>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <div>
                              <div className="flex justify-between items-center">
                                <h4 className="text-sm font-semibold">Main Ingredients</h4>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 px-2 text-xs"
                                  onClick={() => setShowGroceryFor(showGroceryFor === index ? null : index)}
                                >
                                  {showGroceryFor === index ? "Hide Grocery List" : "View Grocery List"}
                                </Button>
                              </div>
                              <ul className="text-sm mt-1 grid grid-cols-1 md:grid-cols-2 gap-1">
                                {meal.ingredients.slice(0, 4).map((ingredient, i) => (
                                  <li key={i} className="flex items-start">
                                    <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 mr-2" />
                                    {ingredient}
                                  </li>
                                ))}
                                {meal.ingredients.length > 4 && (
                                  <li className="text-muted-foreground text-xs flex items-center">
                                    <ArrowRight className="h-3 w-3 mr-1" /> 
                                    {meal.ingredients.length - 4} more ingredients
                                  </li>
                                )}
                              </ul>
                            </div>
                            
                            {/* Grocery List */}
                            {showGroceryFor === index && meal.recipe && (
                              <GroceryList 
                                title={meal.name} 
                                ingredients={meal.recipe.ingredients} 
                              />
                            )}
                            
                            {/* Recipe Button */}
                            <div className="flex justify-between items-center mt-3">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-xs"
                                onClick={() => setShowRecipeFor(showRecipeFor === index ? null : index)}
                              >
                                {showRecipeFor === index ? "Hide Recipe" : "View Recipe"}
                              </Button>
                            </div>
                            
                            {/* Recipe Details */}
                            {showRecipeFor === index && meal.recipe && (
                              <RecipeDetails recipe={meal.recipe} />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollArea>
          ) : (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowNutritionDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NutritionDashboard;
